import { type SVGProps } from 'react';

/**
 * The MicaraHR mark, from the Micara mark system (`brand/` at the portfolio root).
 *
 * People Teal squircle, the product glyph, and the Micara chop — a seal in the
 * bottom-left corner with an M knocked out of it, which every app in the suite
 * carries. That seal is what makes twenty different icons read as one company.
 *
 * This file is GENERATED: `node brand/tools/component.mjs micarahr`. Change the
 * glyph or the ramp in `brand/tools/system.mjs`, rebuild, and regenerate — do
 * not edit the paths here, or this will silently disagree with the favicon and
 * the app icon.
 */
export function BrandMark({
  mono = false,
  ...props
}: SVGProps<SVGSVGElement> & {
  /** Drop the tile and draw the glyph in currentColor. */ mono?: boolean;
}) {
  const ink = mono ? 'currentColor' : '#ffffff';
  return (
    <svg viewBox="0 0 64 64" role="img" aria-label="MicaraHR" {...props}>
      {!mono && (
        <>
          <defs>
            <linearGradient
              id="micarahrGrad"
              x1="0"
              y1="0"
              x2="64"
              y2="64"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#2DD4BF" />
              <stop offset="0.55" stopColor="#0D9488" />
              <stop offset="1" stopColor="#115E59" />
            </linearGradient>
            <radialGradient id="micarahrSheen" cx="0.28" cy="0.08" r="0.9">
              <stop stopColor="#ffffff" stopOpacity="0.28" />
              <stop offset="0.62" stopColor="#ffffff" stopOpacity="0" />
            </radialGradient>
          </defs>
          <rect width="64" height="64" rx="17.5" fill="url(#micarahrGrad)" />
          <rect width="64" height="64" rx="17.5" fill="url(#micarahrSheen)" />
        </>
      )}
      <g strokeLinecap="round" strokeLinejoin="round">
        <circle cx={28} cy={20.5} r={6} fill="none" stroke={ink} strokeWidth={3.4} />
        <path
          d="M16.5 42c0-8 5.2-12.5 11.5-12.5S39.5 34 39.5 42"
          fill="none"
          stroke={ink}
          strokeWidth={3.4}
        />
        <path d="M41 16.5a5 5 0 0 1 0 9.5" fill="none" stroke={ink} strokeWidth={3.4} />
        <path d="M43 31.5c3.5 1.4 5.5 5 5.5 10.5" fill="none" stroke={ink} strokeWidth={3.4} />
      </g>
      {/* The Micara chop — a seal with the M knocked out, on every app in the suite. */}
      <path
        d="M10.6 46.9a3.3 3.3 0 0 1 3.3-3.3h5.2a3.3 3.3 0 0 1 3.3 3.3v5.2a3.3 3.3 0 0 1-3.3 3.3h-5.2a3.3 3.3 0 0 1-3.3-3.3ZM13.2 52.3V46.7h1.9l1.4 2.2 1.4-2.2h1.9v5.6h-1.7v-2.7l-1.6 2.3-1.6-2.3v2.7Z"
        fill={ink}
        fillRule="evenodd"
        opacity={0.3}
      />
    </svg>
  );
}

/** Mark plus wordmark: "Micara" in ink, the product in its own colour. */
export function BrandLockup({
  className = '',
  markClassName = 'h-8 w-8',
  wordClassName = 'text-base',
  onDark = false,
}: {
  className?: string;
  markClassName?: string;
  wordClassName?: string;
  onDark?: boolean;
}) {
  return (
    <span className={`inline-flex items-center gap-2.5 ${className}`}>
      <BrandMark className={`shrink-0 ${markClassName}`} />
      <span
        className={`font-semibold tracking-tight ${wordClassName} ${onDark ? 'text-white' : 'text-slate-900'}`}
      >
        Micara
        <span style={{ color: onDark ? '#2DD4BF' : '#0D9488' }}>HR</span>
      </span>
    </span>
  );
}
